import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import CartReviewTile from './CartReviewTile'
import CartReviewBanner from './CartReviewBanner'
import useUserCartSubscriptionItemData from 'hooks/UserCart/useUserCartSubscriptionItemData'
import LuxPlusProductPlaceholder from 'luxPlus/components/LuxPlusProductPlaceholder'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { addYears } from 'lib/datetime/dateUtils'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import LuxPlusSubscriptionCancellationModal from 'luxPlus/components/LuxPlusSubscriptionCancellationModal'
import ModalContext from 'contexts/ModalContext'
import { CartItemData } from 'hooks/UserCart/useAllUserCartItemsMetadata'
import { calculateHotelLuxPlusDetails, filterOutAddonItems } from './CartReviewUtils'
import Places from 'constants/places'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import LuxPlusCartReviewPriceStack from './LuxPlusCartReviewPriceStack'
import { userCartItemsSaveForLater, userCartItemsDelete } from 'actions/UserCartActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import { multiCartDeleteItemEvent, multiCartSaveItemForLaterEvent } from 'analytics/snowplow/events'
import DeletedCartReviewTile from './DeletedCartReviewTile'
import CondensedCartReviewTile from './CondensedCartReviewTile'
import CondensedCartReviewTileContent from './CondensedCartReviewTileContent'
import CartReviewTileContent from './CartReviewTileContent'
import LoadingCartReviewTileSkeleton from './LoadingCartReviewTileSkeleton'
import CartReviewTileActions from './CartReviewTileActions'

const memberPriceFilter: App.OfferListFilters = {
  offerTypes: ['hotel', 'tactical_ao_hotel'],
  luxPlusFeatures: ['Member prices'],
  destinations: [Places.Anywhere.id],
  sortBy: 'luxPlusList',
}

const searchLink = `/search?${getSearchQueryFromFilter(memberPriceFilter)}`
interface Props {
  item: App.Checkout.LuxPlusSubscriptionItem
  cartItemsData: Array<CartItemData>
  /**
   * @default default
   */
  tileVariant?: 'default' | 'condensed'
}

const renewalDate = addYears(new Date(), 1)

function LuxPlusCartReviewTile({ item, cartItemsData, tileVariant = 'default' }: Props) {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const data = useUserCartSubscriptionItemData(item)

  useEffect(() => {
    if (!!data.error && !data.loading && !data.isSavedForLater) {
      dispatch(userCartItemsSaveForLater([item.itemId]))
    }
  }, [data.error, data.loading, data.isSavedForLater, dispatch, item.itemId])

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartSaveItemForLaterEvent(item, data.isSavedForLater)
    dispatch(userCartItemsSaveForLater(nonNullable([item.itemId])))
  }, [dispatch, item, data.isSavedForLater])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartDeleteItemEvent(item)
    dispatch(userCartItemsDelete(nonNullable([item.itemId])))
  }, [dispatch, item])

  const { totalSavings } = useMemo(() => {
    return calculateHotelLuxPlusDetails(filterOutAddonItems(cartItemsData))
  }, [cartItemsData])

  const onShowCancellationPolicy = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.stopPropagation()
    event.preventDefault()
    showModal(<LuxPlusSubscriptionCancellationModal />)
  }, [showModal])

  if (data.loading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (data.isDeleted) {
    return <DeletedCartReviewTile title="LuxPlus+ Yearly Membership" itemIds={item.itemId} />
  }

  if (tileVariant === 'condensed') {
    return <CondensedCartReviewTile
      to="/lux-plus"
      imageElement={<LuxPlusProductPlaceholder />}
      content={<CondensedCartReviewTileContent />}
      price={<LuxPlusCartReviewPriceStack
        price={data.itemTotals?.price ?? 0}
        joiningFee={data.joiningFee?.amount ?? 0}
        waiveFee={!!data.joiningFee?.waived}
        tileVariant="condensed"
      />}
      banner={<CartReviewBanner
        variant={(!data.isDeleted && data.isSavedForLater) ? 'luxplus' : undefined}
        savings={totalSavings}
        to={searchLink}
      />}
      onDelete={handleDelete}
    />
  }

  return <CartReviewTile
    to="/lux-plus"
    content={<CartReviewTileContent
      title="LuxPlus+ Yearly Membership"
      metaData={<BodyText variant="medium" colour="neutral-two">
        Renews <FormatDateTime value={renewalDate} format="dmy-casual-short" />
      </BodyText>}
      cancellationPolicy={<TextLink weight="bold" colour="highlight-secondary" size="medium" endIcon={<LineInfoCircleIcon colour="neutral-one" />} onClick={onShowCancellationPolicy}>
        Cancel renewal anytime
      </TextLink>}
    />}
    imageElement={<LuxPlusProductPlaceholder />}
    price={<LuxPlusCartReviewPriceStack
      price={data.itemTotals?.price ?? 0}
      joiningFee={data.joiningFee?.amount ?? 0}
      waiveFee={!!data.joiningFee?.waived}
      tileVariant="default"
    />}
    banner={<CartReviewBanner
      variant={(!data.isDeleted && data.isSavedForLater) ? 'luxplus' : undefined}
      savings={totalSavings}
      to={searchLink}
    />}
    isBookable
    saveForLater={data.isSavedForLater}
    actions={<CartReviewTileActions
      isBookable
      saveForLater={data.isSavedForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />}
  />
}

export default LuxPlusCartReviewTile
